/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: aiProfilesActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the AI Integration module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for AI Profiles module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate an AI Profile in the form.
 * Pattern: /admin/ai-integration/profiles/new?duplicate={id}
 */
export const getAiProfileDuplicateUrl = (id: string | number): string =>
  `/admin/ai-integration/profiles/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given AI Profile id.
 */
export const navigateToAiProfileDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getAiProfileDuplicateUrl(id));
};

/**
 * Low-level API helper to delete an AI Profile by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteAiProfileById = async (
  id: string | number,
  tenantSlug?: string,
  userId?: string | number | null,
): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/ai/profiles/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(tenantSlug, userId),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete AI Profile';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};


